<script setup lang="ts">
import { ref } from 'vue'

import { Field, Pitch } from '@collector/shared-feature-pitch'
import {
  Coordinates,
  getCoordinatesWithTeamSideChanged,
  getMouseEventCoordinates,
} from '@collector/shared-ui-domain'
import {
  useBetStatus,
  useEventSettings,
} from '@desktop/views/Relation/composables'
import { useBlockades } from '@desktop/views/Relation/Sports/composables'

import EventStatusIncidentsOnField from './EventStatusIncidentsOnField/EventStatusIncidentsOnField.vue'
import PitchStatusOverlays from './PitchStatusOverlays.vue'
import PossessionIndicator from './PossessionIndicator.vue'

interface Props {
  pitchImageSrc: string
  pitchImageClass?: string
  fieldClass?: string
  fieldUnderlaysClass?: string
  possessionHomeSrc?: string
  possessionAwaySrc?: string
  isPitchStatusOverlaysOnBottom?: boolean
}

defineProps<Props>()

// emits
type Emits = {
  fieldClickCoordinates: [event: MouseEvent, coordinates: Coordinates]
}

const emit = defineEmits<Emits>()

// composables
const { betStatusBackgroundClass } = useBetStatus()
const { isPitchBlocked } = useBlockades()
const { eventSettings } = useEventSettings()

// state
const eventStatusIncidentsOnFieldVisible = ref(false)

// methods
function onFieldClick(params: {
  event: MouseEvent
  targetElement: HTMLElement
}): void {
  const { event, targetElement } = params
  if (!isPitchBlocked()) {
    const coordinates = getCoordinatesWithTeamSideChanged(
      getMouseEventCoordinates(event, targetElement),
      !!eventSettings.value.teamSideChanged,
    )

    event.preventDefault()
    // eslint-disable-next-line no-console
    console.debug('onFieldClick', JSON.stringify(coordinates), +new Date())
    emit('fieldClickCoordinates', event, coordinates)
  }
}
</script>

<template>
  <Pitch
    class="rounded-md transition-colors duration-200"
    :class="betStatusBackgroundClass"
    :fieldClass
    :fieldUnderlaysClass
    :pitchImageSrc
    :pitchImageClass="`${isPitchBlocked() && !eventStatusIncidentsOnFieldVisible ? 'opacity-30' : ''} ${pitchImageClass}`"
    data-testid="pitch"
  >
    <template #overlays>
      <Field
        :fieldClass
        @click="onFieldClick"
      >
        <PossessionIndicator
          v-if="possessionHomeSrc && possessionAwaySrc"
          :possessionHomeSrc
          :possessionAwaySrc
        />
        <EventStatusIncidentsOnField
          @visible="eventStatusIncidentsOnFieldVisible = $event"
        />
        <PitchStatusOverlays
          :isOnBottom="
            eventStatusIncidentsOnFieldVisible || isPitchStatusOverlaysOnBottom
          "
        />
        <slot
          v-if="!eventStatusIncidentsOnFieldVisible"
          name="overlays"
        />
      </Field>
    </template>
  </Pitch>
</template>
